/* Admin Payment Book — manual publisher payout management.
   Workflow: enter details → Process & Save → click Send to email the
   branded payout advice → manually Mark Paid to close the books.
   No receipts, no attachments — just data + branded HTML email. */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import AdminShell from "../components/admin/AdminShell";
import { authFetch, getAdminToken } from "../utils/adminAuth";
import {
  BookOpen,
  Search,
  Download,
  Send,
  Trash2,
  Copy,
  X,
  Wallet,
  Landmark,
  ChevronDown,
  Sparkles,
  Save,
  CheckCircle,
  FileText,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const EST_TZ = "America/New_York";

// Payment cycle taxonomy — kept in lock-step with the backend `CYCLE_LABELS`
// in payments.py. Legacy "monthly" rows still render as "Monthly Net 10".
const CYCLE_OPTIONS = [
  { key: "weekly_net7", label: "Weekly Net 7" },
  { key: "monthly_net10", label: "Monthly Net 10" },
  { key: "days_15", label: "15 Days Pay" },
];
const cycleLabel = (key) => {
  if (key === "monthly") return "Monthly Net 10";
  return CYCLE_OPTIONS.find((c) => c.key === key)?.label
    || (key || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

const formatMoney = (v) => `$${(Number(v) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const authFetch_ = async (url, opts = {}) => {
  const path = url.startsWith(API) ? url.slice(API.length) : url;
  const headers = { ...(opts.headers || {}) };
  if (!headers["Content-Type"] && opts.body && typeof opts.body === "string") headers["Content-Type"] = "application/json";
  const res = await authFetch(path, { ...opts, headers });
  if (!res.ok) {
    const t = await res.text();
    throw new Error(`HTTP ${res.status}: ${t.slice(0, 200)}`);
  }
  const ct = res.headers.get("content-type") || "";
  return ct.includes("application/json") ? res.json() : res;
};

const useCopyToClipboard = () => {
  const [copied, setCopied] = useState("");
  const copy = useCallback((value, label) => {
    if (!value) return;
    navigator.clipboard?.writeText(value);
    setCopied(label || value);
    setTimeout(() => setCopied(""), 1500);
  }, []);
  return { copied, copy };
};

/* ── Unified New Payout Form ─────────────────────────────────────── */
const UnifiedPayoutForm = ({ onSaved }) => {
  const empty = {
    name: "",
    email: "",
    bank_name: "",
    account_holder: "",
    bank_address: "",
    account_number: "",
    routing_number: "",
    swift_code: "",
    amount: "",
    cycle: "weekly_net7",
    period_from: "",
    period_to: "",
    internal_notes: "",
  };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [msgKind, setMsgKind] = useState("info");
  const [pasteText, setPasteText] = useState("");
  const [pasting, setPasting] = useState(false);
  const [emailLoaded, setEmailLoaded] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    setEmailLoaded(false);
    if (!form.email || !/\S+@\S+\.\S+/.test(form.email)) return;
    const t = setTimeout(async () => {
      try {
        const data = await authFetch_(
          `${API}/api/admin/bank-details/lookup?email=${encodeURIComponent(form.email)}`
        );
        setForm((f) => ({
          ...f,
          name: f.name || data.name || "",
          bank_name: f.bank_name || data.bank_name || "",
          bank_address: f.bank_address || data.bank_address || "",
          account_holder: f.account_holder || data.account_holder || "",
          account_number: f.account_number || data.account_number || "",
          routing_number: f.routing_number || data.routing_number || "",
          swift_code: f.swift_code || data.swift_code || "",
          // Pre-select the publisher's usual payout cycle when known
          // (only overrides the default; user's edit is preserved).
          cycle: f._cycle_touched ? f.cycle : (data.last_cycle || f.cycle),
        }));
        setEmailLoaded(true);
      } catch {
        setEmailLoaded(false);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [form.email]);

  const smartPaste = async () => {
    if (!pasteText.trim()) return;
    setPasting(true);
    setMsg("");
    try {
      const parsed = await authFetch_(`${API}/api/admin/bank-details/smart-paste`, {
        method: "POST",
        body: JSON.stringify({ text: pasteText }),
      });
      setForm((f) => {
        const next = { ...f };
        for (const [k, v] of Object.entries(parsed)) {
          if (v && !next[k]) next[k] = v;
        }
        return next;
      });
      setMsgKind("success");
      setMsg("AI extracted fields — review the form below, then Process & Save.");
    } catch (err) {
      setMsgKind("error");
      setMsg(`AI extraction failed: ${err.message}`);
    } finally {
      setPasting(false);
    }
  };

  const process = async (e) => {
    e?.preventDefault?.();
    setMsg("");
    if (!form.email || !form.name || !form.amount || !form.period_from || !form.period_to) {
      setMsgKind("error");
      setMsg("Fill Name, Email, Amount, and both Period dates before saving.");
      return;
    }
    setSaving(true);
    try {
      const body = {
        name: form.name,
        email: form.email,
        bank_name: form.bank_name || null,
        bank_address: form.bank_address || null,
        account_holder: form.account_holder || null,
        account_number: form.account_number || null,
        routing_number: form.routing_number || null,
        swift_code: form.swift_code || null,
        amount: parseFloat(form.amount),
        cycle: form.cycle,
        period_from: form.period_from,
        period_to: form.period_to,
        internal_notes: form.internal_notes || null,
      };
      const res = await authFetch_(`${API}/api/admin/payments/unified`, {
        method: "POST",
        body: JSON.stringify(body),
      });
      setMsgKind("success");
      setMsg(`Saved — Invoice ${res?.payment?.invoice_number || ""}. Click Send in the ledger to email the publisher.`);
      setForm(empty);
      setPasteText("");
      setEmailLoaded(false);
      onSaved?.(res);
    } catch (err) {
      setMsgKind("error");
      setMsg(err.message);
    } finally {
      setSaving(false);
    }
  };

  const msgColor =
    msgKind === "success"
      ? "text-emerald-400"
      : msgKind === "error"
      ? "text-red-400"
      : "text-slate-400";

  return (
    <form
      onSubmit={process}
      className="bg-slate-900 rounded-xl border border-slate-800 p-4 sm:p-5 mb-4"
      data-testid="unified-payout-form"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-white flex items-center gap-2">
          <Landmark className="w-4 h-4 text-blue-400" /> New Payout
          <span className="text-[11px] text-slate-500 font-normal">
            · Bank details + payment saved together
          </span>
        </h3>
      </div>

      {/* AI Smart Paste */}
      <div className="bg-slate-800/50 border border-dashed border-blue-500/40 rounded-lg p-3 mb-4">
        <div className="text-[10px] uppercase tracking-widest text-blue-400 font-semibold mb-1.5 flex items-center gap-1">
          <Sparkles className="w-3 h-3" /> AI Smart Paste
        </div>
        <textarea
          rows={2}
          value={pasteText}
          onChange={(e) => setPasteText(e.target.value)}
          placeholder="Paste raw bank details (any format — email signature, invoice, chat) and click Auto-Fill…"
          className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-2 text-slate-100 text-xs font-mono"
          data-testid="smart-paste-input"
        />
        <button
          type="button"
          onClick={smartPaste}
          disabled={pasting || !pasteText.trim()}
          className="mt-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white text-xs font-semibold px-3 py-1.5 rounded"
          data-testid="smart-paste-run"
        >
          {pasting ? "Extracting…" : "Auto-Fill with AI"}
        </button>
      </div>

      {/* Publisher */}
      <div className="mb-4">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">
          Publisher
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
          <input
            required
            type="email"
            placeholder="Publisher Email *"
            value={form.email}
            onChange={set("email")}
            className="bg-slate-800 border border-slate-700 rounded px-3 py-2 text-slate-100 font-mono text-xs"
            data-testid="unified-email"
          />
          <input
            required
            placeholder="Publisher Name *"
            value={form.name}
            onChange={set("name")}
            className="bg-slate-800 border border-slate-700 rounded px-3 py-2 text-slate-100"
            data-testid="unified-name"
          />
        </div>
        {emailLoaded && (
          <div className="text-[11px] text-emerald-400 mt-1.5" data-testid="unified-bank-loaded">
            ✓ Existing bank details loaded — edit any field below to update them.
          </div>
        )}
      </div>

      {/* Bank Details */}
      <div className="mb-4">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">
          Bank Details
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
          <input
            placeholder="Bank Name"
            value={form.bank_name}
            onChange={set("bank_name")}
            className="bg-slate-800 border border-slate-700 rounded px-3 py-2 text-slate-100"
            data-testid="unified-bank-name"
          />
          <input
            placeholder="Account Holder"
            value={form.account_holder}
            onChange={set("account_holder")}
            className="bg-slate-800 border border-slate-700 rounded px-3 py-2 text-slate-100"
            data-testid="unified-account-holder"
          />
          <input
            placeholder="Bank Address"
            value={form.bank_address}
            onChange={set("bank_address")}
            className="sm:col-span-2 bg-slate-800 border border-slate-700 rounded px-3 py-2 text-slate-100"
            data-testid="unified-bank-address"
          />
          <input
            placeholder="Account Number"
            value={form.account_number}
            onChange={set("account_number")}
            className="bg-slate-800 border border-slate-700 rounded px-3 py-2 text-slate-100 font-mono"
            data-testid="unified-account-number"
          />
          <input
            placeholder="Routing Number"
            value={form.routing_number}
            onChange={set("routing_number")}
            className="bg-slate-800 border border-slate-700 rounded px-3 py-2 text-slate-100 font-mono"
            data-testid="unified-routing"
          />
          <input
            placeholder="SWIFT Code"
            value={form.swift_code}
            onChange={set("swift_code")}
            className="sm:col-span-2 bg-slate-800 border border-slate-700 rounded px-3 py-2 text-slate-100 font-mono"
            data-testid="unified-swift"
          />
        </div>
      </div>

      {/* Payment */}
      <div className="mb-4">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">
          Payment · America/New_York (EST)
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
          <input
            required
            type="number"
            step="0.01"
            placeholder="Amount USD *"
            value={form.amount}
            onChange={set("amount")}
            className="bg-slate-800 border border-slate-700 rounded px-3 py-2 text-slate-100 tabular-nums"
            data-testid="unified-amount"
          />
          <select
            value={form.cycle}
            onChange={(e) => setForm((f) => ({ ...f, cycle: e.target.value, _cycle_touched: true }))}
            className="bg-slate-800 border border-slate-700 rounded px-3 py-2 text-slate-100"
            data-testid="unified-cycle"
          >
            {CYCLE_OPTIONS.map((c) => (
              <option key={c.key} value={c.key}>{c.label}</option>
            ))}
          </select>
          <input
            required
            type="date"
            value={form.period_from}
            onChange={set("period_from")}
            title="From (EST)"
            className="bg-slate-800 border border-slate-700 rounded px-3 py-2 text-slate-100"
            data-testid="unified-period-from"
          />
          <input
            required
            type="date"
            value={form.period_to}
            onChange={set("period_to")}
            title="To (EST)"
            className="bg-slate-800 border border-slate-700 rounded px-3 py-2 text-slate-100"
            data-testid="unified-period-to"
          />
        </div>
        <textarea
          placeholder="Internal notes (admin-only, not emailed)"
          value={form.internal_notes}
          onChange={set("internal_notes")}
          rows={2}
          className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-slate-100 text-xs mt-2"
          data-testid="unified-notes"
        />
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div className={`text-xs ${msgColor}`} data-testid="unified-msg">
          {msg || " "}
        </div>
        <button
          type="submit"
          disabled={saving}
          className="inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-semibold text-sm px-5 py-2.5 rounded-lg"
          data-testid="unified-save"
        >
          <Save className="w-4 h-4" />
          {saving ? "Processing…" : "Process & Save"}
        </button>
      </div>
    </form>
  );
};

/* ── Read-only Publisher Directory ───────────────────────────────── */
const BankDirectoryList = ({ items, reload }) => {
  const remove = async (id) => {
    if (!window.confirm("Delete this bank record?")) return;
    await authFetch_(`${API}/api/admin/bank-details/${id}`, { method: "DELETE" });
    reload();
  };
  return (
    <div className="bg-slate-900 rounded-xl border border-slate-800 p-4 overflow-x-auto" data-testid="bank-list">
      <table className="w-full text-xs">
        <thead className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-800">
          <tr>
            <th className="text-left py-2">Publisher</th>
            <th className="text-left py-2">Bank</th>
            <th className="text-left py-2">Routing / SWIFT</th>
            <th className="text-right py-2">Actions</th>
          </tr>
        </thead>
        <tbody>
          {items.map((r) => (
            <tr key={r.id} className="border-b border-slate-800/60 hover:bg-slate-800/40">
              <td className="py-2">
                <div className="text-slate-100 font-medium">{r.name}</div>
                <div className="text-slate-500 font-mono">{r.email}</div>
              </td>
              <td className="py-2 text-slate-300">
                {r.bank_name || "—"}
                {r.account_number && (
                  <div className="text-[10px] text-slate-500 font-mono">
                    ••••{(r.account_number || "").slice(-4)}
                  </div>
                )}
              </td>
              <td className="py-2 text-slate-300 font-mono text-[11px]">
                <div>{r.routing_number || "—"}</div>
                <div className="text-slate-500">{r.swift_code || ""}</div>
              </td>
              <td className="py-2 text-right">
                <button
                  onClick={() => remove(r.id)}
                  className="text-red-400 hover:text-red-300"
                  data-testid={`delete-bank-${r.id}`}
                  title="Delete"
                >
                  <Trash2 className="w-3.5 h-3.5 inline" />
                </button>
              </td>
            </tr>
          ))}
          {items.length === 0 && (
            <tr>
              <td colSpan={4} className="text-center py-8 text-slate-500">
                No bank records yet — create one via the New Payout form above.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

/* ── Bank Info Popup ─────────────────────────────────────────────── */
const BankInfoPopup = ({ row, bank, onClose, copy, copied }) => {
  const item = (label, value, testid) =>
    value ? (
      <button
        onClick={() => copy(value, label)}
        className="w-full flex items-center justify-between bg-slate-800 hover:bg-slate-700 rounded-md px-3 py-2 text-left text-xs group"
        data-testid={testid}
      >
        <span>
          <span className="block text-[10px] uppercase tracking-widest text-slate-500">{label}</span>
          <span className="block font-mono text-slate-100 mt-0.5 break-all">{value}</span>
        </span>
        <Copy className="w-3.5 h-3.5 text-slate-500 group-hover:text-blue-400" />
      </button>
    ) : null;

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-slate-900 rounded-xl border border-slate-800 shadow-2xl w-full max-w-sm p-4"
        onClick={(e) => e.stopPropagation()}
        data-testid="bank-info-popup"
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-white">Bank Info — {row.name}</h3>
          <button onClick={onClose}>
            <X className="w-4 h-4 text-slate-400 hover:text-white" />
          </button>
        </div>
        {bank ? (
          <div className="space-y-2">
            {item("Bank Name", bank.bank_name, "copy-bank-name")}
            {item("Bank Address", bank.bank_address, "copy-bank-address")}
            {item("Account Holder", bank.account_holder, "copy-account-holder")}
            {item("Account Number", bank.account_number, "copy-account-number")}
            {item("Routing Number", bank.routing_number, "copy-routing")}
            {item("SWIFT Code", bank.swift_code, "copy-swift")}
          </div>
        ) : (
          <div className="text-sm text-slate-400">
            No bank details on file for <span className="font-mono text-slate-200">{row.email}</span>.
          </div>
        )}
        {copied && <div className="mt-3 text-xs text-emerald-400 font-medium">Copied {copied} to clipboard ✓</div>}
      </div>
    </div>
  );
};

/* ── Main Page ───────────────────────────────────────────────────── */
export default function AdminPaymentsPage() {
  const [payments, setPayments] = useState({ rows: [], total_paid: 0, total_pending: 0 });
  const [banks, setBanks] = useState([]);
  const [q, setQ] = useState("");
  const [year, setYear] = useState("");
  const [month, setMonth] = useState("");
  const [cycle, setCycle] = useState("");
  const [status, setStatus] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [popup, setPopup] = useState(null);
  const [previewFor, setPreviewFor] = useState(null);
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(new Set());
  const { copied, copy } = useCopyToClipboard();

  const bankByEmail = useMemo(() => {
    const m = {};
    for (const b of banks) m[b.email] = b;
    return m;
  }, [banks]);

  const loadPayments = useCallback(async () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (year) params.set("year", year);
    if (month) params.set("month", month);
    if (cycle) params.set("cycle", cycle);
    if (status) params.set("status", status);
    const data = await authFetch_(`${API}/api/admin/payments?${params}`);
    setPayments(data);
  }, [q, year, month, cycle, status]);

  const loadBanks = useCallback(async () => {
    const data = await authFetch_(`${API}/api/admin/bank-details`);
    setBanks(data);
  }, []);

  useEffect(() => { loadBanks(); }, [loadBanks]);
  useEffect(() => { loadPayments(); }, [loadPayments]);

  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected((s) => (s.size === payments.rows.length ? new Set() : new Set(payments.rows.map((r) => r.id))));

  const sendBatch = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Mark ${selected.size} row(s) as Paid and send notification email to each publisher?`)) return;
    const res = await authFetch_(`${API}/api/admin/payments/batch/send-receipts`, {
      method: "POST",
      body: JSON.stringify({ row_ids: [...selected] }),
    });
    window.alert(`Marked paid & notified: ${res.sent}. Failed: ${res.failed}.`);
    setSelected(new Set());
    loadPayments();
  };

  const removeRow = async (id) => {
    if (!window.confirm("Delete this payment record?")) return;
    await authFetch_(`${API}/api/admin/payments/${id}`, { method: "DELETE" });
    loadPayments();
  };

  const markPaid = async (row) => {
    if (
      !window.confirm(
        `Mark as Paid and send notification email to ${row.email}?`
      )
    ) {
      return;
    }
    setSending((s) => new Set([...s, row.id]));
    try {
      await authFetch_(`${API}/api/admin/payments/${row.id}/mark-paid`, {
        method: "POST",
      });
      loadPayments();
    } catch (e) {
      window.alert(e.message);
    }
    setSending((s) => { const n = new Set(s); n.delete(row.id); return n; });
  };

  const markUnpaid = async (id) => {
    if (!window.confirm("Revert this row back to Unpaid?")) return;
    await authFetch_(`${API}/api/admin/payments/${id}/mark-unpaid`, { method: "POST" });
    loadPayments();
  };

  const openPreview = async (row) => {
    setPreviewFor(row);
    setPreview(null);
    try {
      const data = await authFetch_(`${API}/api/admin/payments/${row.id}/email-preview`);
      setPreview(data);
    } catch (e) {
      window.alert(e.message);
      setPreviewFor(null);
    }
  };

  const csvExport = () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (year) params.set("year", year);
    if (month) params.set("month", month);
    if (cycle) params.set("cycle", cycle);
    if (status) params.set("status", status);
    const token = getAdminToken();
    fetch(`${API}/api/admin/payments/export.csv?${params}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `payments_${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      });
  };

  const onUnifiedSaved = async () => {
    await Promise.all([loadPayments(), loadBanks()]);
  };

  // Internal billing record — admin-only PDF, opened in a new tab.
  const [invoiceLoading, setInvoiceLoading] = useState(new Set());
  const openInvoice = async (r) => {
    setInvoiceLoading((prev) => new Set(prev).add(r.id));
    try {
      const token = getAdminToken();
      const res = await fetch(`${API}/api/admin/payments/${r.id}/invoice.pdf`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      window.alert(`Invoice unavailable: ${e.message}`);
    } finally {
      setInvoiceLoading((prev) => { const n = new Set(prev); n.delete(r.id); return n; });
    }
  };

  const years = [];
  const nowY = new Date().toLocaleString("en-US", { timeZone: EST_TZ, year: "numeric" });
  for (let y = Number(nowY); y >= Number(nowY) - 3; y--) years.push(y);

  return (
    <AdminShell pageTitle="Payment Book" pageDescription="Manual publisher payout ledger — dates in America/New_York (EST)">
      {/* Summary tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-4">
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Total Paid</div>
          <div className="text-2xl font-bold text-emerald-400 tabular-nums mt-1" data-testid="total-paid">{formatMoney(payments.total_paid)}</div>
        </div>
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-4">
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Total Pending</div>
          <div className="text-2xl font-bold text-amber-400 tabular-nums mt-1" data-testid="total-pending">{formatMoney(payments.total_pending)}</div>
        </div>
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-4">
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Records (filtered)</div>
          <div className="text-2xl font-bold text-slate-100 tabular-nums mt-1">{payments.count || 0}</div>
        </div>
      </div>

      <UnifiedPayoutForm onSaved={onUnifiedSaved} />

      {/* Filter bar */}
      <div className="bg-slate-900 rounded-xl border border-slate-800 p-3 mb-4 flex flex-wrap items-center gap-2 text-sm">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email…" className="w-full pl-9 pr-3 py-2 bg-slate-800 border border-slate-700 rounded-md text-slate-100" data-testid="payments-search" />
        </div>
        <select value={month} onChange={(e) => setMonth(e.target.value)} className="bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-slate-100" data-testid="filter-month">
          <option value="">All Months</option>
          {[1,2,3,4,5,6,7,8,9,10,11,12].map((m) => <option key={m} value={m}>{new Date(2000, m-1, 1).toLocaleString("en-US", { month: "long" })}</option>)}
        </select>
        <select value={year} onChange={(e) => setYear(e.target.value)} className="bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-slate-100" data-testid="filter-year">
          <option value="">All Years</option>
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <select value={cycle} onChange={(e) => setCycle(e.target.value)} className="bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-slate-100" data-testid="filter-cycle">
          <option value="">All Cycles</option>
          {CYCLE_OPTIONS.map((c) => (
            <option key={c.key} value={c.key}>{c.label}</option>
          ))}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-slate-100" data-testid="filter-status">
          <option value="">All Status</option>
          <option value="paid">Paid</option>
          <option value="unpaid">Unpaid</option>
        </select>
        <button onClick={csvExport} className="ml-auto inline-flex items-center gap-1.5 bg-slate-800 hover:bg-slate-700 text-slate-100 text-sm px-3 py-2 rounded-md border border-slate-700" data-testid="csv-export">
          <Download className="w-4 h-4" /> CSV
        </button>
        {selected.size > 0 && (
          <button onClick={sendBatch} className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-500 text-white text-sm px-3 py-2 rounded-md" data-testid="batch-send">
            <CheckCircle className="w-4 h-4" /> Mark {selected.size} Paid &amp; Notify
          </button>
        )}
      </div>

      {/* Ledger table — no more Receipt column */}
      <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-x-auto mb-4">
        <table className="w-full text-xs">
          <thead className="bg-slate-800/60 text-[10px] uppercase tracking-wider text-slate-400">
            <tr>
              <th className="p-2 w-8"><input type="checkbox" checked={selected.size > 0 && selected.size === payments.rows.length} onChange={toggleAll} /></th>
              <th className="text-left p-2">Invoice</th>
              <th className="text-left p-2">Publisher</th>
              <th className="text-right p-2">Amount</th>
              <th className="text-left p-2">Cycle</th>
              <th className="text-left p-2">Period (EST)</th>
              <th className="text-left p-2">Routing / SWIFT</th>
              <th className="text-left p-2">Email</th>
              <th className="text-left p-2">Status</th>
              <th className="text-center p-2">Record</th>
              <th className="text-right p-2">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800/60">
            {payments.rows.map((r) => {
              const emailSent = !!(r.email_sent_at || r.receipt_sent_at);
              return (
              <tr key={r.id} className={`hover:bg-slate-800/40 ${selected.has(r.id) ? "bg-slate-800/60" : ""}`}>
                <td className="p-2"><input type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} /></td>
                <td className="p-2 font-mono text-blue-400 text-[11px] tracking-wider whitespace-nowrap" data-testid={`invoice-${r.id}`}>{r.invoice_number || "—"}</td>
                <td className="p-2">
                  <div className="text-slate-100 font-medium">{r.name}</div>
                  <div className="text-slate-500 font-mono">{r.email}</div>
                  {r.internal_notes && <div className="text-slate-500 italic text-[10px] mt-1 line-clamp-2">📝 {r.internal_notes}</div>}
                </td>
                <td className="p-2 text-right tabular-nums font-semibold text-emerald-400">{formatMoney(r.amount)}</td>
                <td className="p-2 text-slate-300">{cycleLabel(r.cycle)}</td>
                <td className="p-2 text-slate-300 font-mono text-[11px]">{r.period_from} → {r.period_to}</td>
                <td className="p-2 text-slate-300 font-mono text-[11px]">
                  {(() => {
                    const b = bankByEmail[r.email];
                    if (!b) return <span className="text-slate-500">—</span>;
                    return (
                      <>
                        <div>{b.routing_number || "—"}</div>
                        <div className="text-slate-500">{b.swift_code || ""}</div>
                      </>
                    );
                  })()}
                </td>
                <td className="p-2 text-[11px]" data-testid={`email-cell-${r.id}`}>
                  {emailSent ? (
                    <span className="inline-flex items-center gap-1 text-emerald-400" title={r.email_sent_at || r.receipt_sent_at}>
                      <Send className="w-3 h-3" /> Sent
                    </span>
                  ) : (
                    <span className="text-slate-500">Not sent</span>
                  )}
                </td>
                <td className="p-2">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${r.status === "paid" ? "bg-emerald-500/15 text-emerald-400" : "bg-amber-500/15 text-amber-400"}`}>
                    {r.status}
                  </span>
                </td>
                <td className="p-2 text-center">
                  <button
                    onClick={() => openInvoice(r)}
                    disabled={invoiceLoading.has(r.id)}
                    className="text-slate-400 hover:text-blue-400 disabled:opacity-50 transition-colors"
                    title="View internal billing record (PDF)"
                    data-testid={`invoice-pdf-${r.id}`}
                  >
                    <FileText className="w-4 h-4 inline" />
                  </button>
                </td>
                <td className="p-2 text-right whitespace-nowrap">
                  <button onClick={() => setPopup(r)} className="mr-2 text-slate-400 hover:text-blue-400" title="Bank Info" data-testid={`bank-info-${r.id}`}><Wallet className="w-4 h-4 inline" /></button>
                  <button onClick={() => openPreview(r)} className="mr-2 text-slate-400 hover:text-emerald-400" title="View email" data-testid={`view-${r.id}`}><BookOpen className="w-4 h-4 inline" /></button>
                  {r.status === "paid" ? (
                    <button onClick={() => markUnpaid(r.id)} className="mr-2 inline-flex items-center gap-1 bg-slate-700 hover:bg-slate-600 text-slate-100 px-2 py-1 rounded text-[11px]" data-testid={`mark-unpaid-${r.id}`} title="Revert to Unpaid">
                      Unpaid
                    </button>
                  ) : (
                    <button
                      onClick={() => markPaid(r)}
                      disabled={sending.has(r.id)}
                      className="mr-2 inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white px-2 py-1 rounded text-[11px]"
                      data-testid={`mark-paid-${r.id}`}
                      title={`Mark as Paid and email ${r.email}`}
                    >
                      <CheckCircle className="w-3 h-3" /> {sending.has(r.id) ? "Sending…" : "Mark Paid"}
                    </button>
                  )}
                  <button onClick={() => removeRow(r.id)} className="text-red-400 hover:text-red-300" title="Delete"><Trash2 className="w-4 h-4 inline" /></button>
                </td>
              </tr>
              );
            })}
            {payments.rows.length === 0 && (
              <tr><td colSpan={11} className="p-8 text-center text-slate-500">No payments match the current filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Read-only Publisher Directory */}
      <details className="mb-6">
        <summary className="cursor-pointer text-slate-200 font-semibold mb-3 flex items-center gap-2 select-none">
          <ChevronDown className="w-4 h-4" /> Publisher Bank Directory ({banks.length})
        </summary>
        <BankDirectoryList items={banks} reload={loadBanks} />
      </details>

      {popup && <BankInfoPopup row={popup} bank={bankByEmail[popup.email]} onClose={() => setPopup(null)} copy={copy} copied={copied} />}

      {previewFor && (
        <div
          className="fixed inset-0 z-50 bg-slate-950/75 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4"
          onClick={() => { setPreviewFor(null); setPreview(null); }}
          data-testid="preview-modal"
        >
          <div className="bg-slate-900 rounded-2xl border border-slate-800 shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-800">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
                  {preview?.already_sent ? `Sent to ${preview.to} on ${new Date(preview.sent_at).toLocaleString("en-US", { timeZone: EST_TZ })}` : `Draft — will be emailed to ${previewFor.email}`}
                </div>
                <div className="text-white font-semibold truncate">{preview?.subject || "Loading…"}</div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => { setPreviewFor(null); setPreview(null); }} className="text-slate-400 hover:text-white p-1"><X className="w-4 h-4" /></button>
              </div>
            </div>
            <div className="bg-white overflow-y-auto flex-1">
              {preview?.html ? (
                <iframe title="email-preview" srcDoc={preview.html} className="w-full h-[75vh]" data-testid="preview-email-frame" />
              ) : (
                <div className="p-6 text-slate-500 text-sm">Loading email preview…</div>
              )}
            </div>
          </div>
        </div>
      )}
    </AdminShell>
  );
}
